import { type Sprite, type Texture } from 'pixi.js';
import { TILE_HALF, tileToScreen } from '../sim/projection';
import { Layer, type Renderable } from '../sim/depth';
import type { GameMap } from '../sim/map';
import type { StaticTextures } from './staticsArt';
import type { TileBounds } from './viewportCull';
import { UoHueSprite } from './uoHueBatch';

/** Complete authored placement data with no retained Pixi display object. */
export interface StaticPlacement extends Renderable {
  id: number;
  roof?: boolean;
  lightTransparent?: true;
  light?: {
    profile: number;
    color: number;
    shader?: number;
  };
  name: string;
  texture: Texture;
}

/**
 * One active static billboard.
 *
 * The stable authored identity lives on {@link placement}; the sprite is a
 * pooled viewport resource and may represent a different placement after it
 * leaves the camera window.
 */
export interface StaticBillboard extends Renderable {
  sprite: Sprite;
  roof?: boolean;
  name?: string;
  placement?: StaticPlacement;
}

export interface StaticWindowUpdate {
  entered: StaticBillboard[];
  left: StaticBillboard[];
}

export const CLASSIC_UO_LIGHT_LIMIT = 100;

/**
 * Spatially indexed authored statics with a viewport-sized sprite pool.
 *
 * Roof and region consumers retain the complete placement array.
 * Only placements in the current cull window own sprites.
 */
export class StaticScene {
  readonly placements: StaticPlacement[];
  readonly activeBillboards: StaticBillboard[] = [];
  private readonly buckets = new Map<string, StaticPlacement[]>();
  private readonly activeById = new Map<number, StaticBillboard>();
  private readonly pool: Sprite[] = [];
  private readonly free: Sprite[] = [];
  private readonly lightWindow: StaticPlacement[] = [];

  constructor(
    map: GameMap,
    textures: StaticTextures,
  ) {
    const placements: StaticPlacement[] = [];
    let id = 0;
    for (const source of map.getStatics()) {
      const texture = textures.get(source.tileName);
      if (!texture) {
        console.warn(
          `no baked static texture for "${source.tileName}" at ${source.x},${source.y} - skipped`,
        );
        continue;
      }
      const placement: StaticPlacement = {
        id,
        texture,
        x: source.x,
        y: source.y,
        z: source.z,
        layer: Layer.Static,
        roof: source.roof,
        foliage: textures.foliage?.has(source.tileName),
        lightTransparent: source.lightTransparent,
        light: source.light,
        name: source.tileName,
      };
      id += 1;
      placements.push(placement);
      const key = `${placement.x},${placement.y}`;
      const bucket = this.buckets.get(key);
      if (bucket) bucket.push(placement);
      else this.buckets.set(key, [placement]);
    }
    this.placements = placements;
  }

  update(bounds: TileBounds | undefined): StaticWindowUpdate {
    const target: StaticPlacement[] = [];
    if (bounds) {
      for (let y = bounds.minY; y <= bounds.maxY; y++) {
        for (let x = bounds.minX; x <= bounds.maxX; x++) {
          const bucket = this.buckets.get(`${x},${y}`);
          if (bucket) target.push(...bucket);
        }
      }
    }
    const targetIds = new Set(target.map((placement) => placement.id));
    const left: StaticBillboard[] = [];
    for (const [id, billboard] of this.activeById) {
      if (targetIds.has(id)) continue;
      this.activeById.delete(id);
      billboard.sprite.removeFromParent();
      billboard.sprite.removeAllListeners();
      billboard.sprite.eventMode = 'none';
      billboard.sprite.cursor = 'auto';
      billboard.sprite.visible = false;
      this.free.push(billboard.sprite);
      left.push(billboard);
    }

    const entered: StaticBillboard[] = [];
    this.activeBillboards.length = 0;
    for (const placement of target) {
      let billboard = this.activeById.get(placement.id);
      if (!billboard) {
        let sprite = this.free.pop();
        if (!sprite) {
          sprite = new UoHueSprite();
          sprite.anchor.set(0.5, 1);
          this.pool.push(sprite);
        }
        sprite.texture = placement.texture;
        const screen = tileToScreen(placement.x, placement.y, placement.z);
        sprite.position.set(screen.x, screen.y + 2 * TILE_HALF);
        sprite.visible = true;
        billboard = {
          sprite,
          x: placement.x,
          y: placement.y,
          z: placement.z,
          layer: placement.layer,
          roof: placement.roof,
          foliage: placement.foliage,
          name: placement.name,
          placement,
        };
        this.activeById.set(placement.id, billboard);
        entered.push(billboard);
      }
      this.activeBillboards.push(billboard);
    }
    return { entered, left };
  }

  get poolSize(): number {
    return this.pool.length;
  }

  /** Return a reused, spatially bounded list of authored light placements. */
  lightsIn(
    bounds: TileBounds | undefined,
    margin: number,
    hiddenRoof: ReadonlySet<StaticPlacement> | null = null,
  ): readonly StaticPlacement[] {
    this.lightWindow.length = 0;
    if (!bounds) return this.lightWindow;
    for (let y = bounds.minY - margin; y <= bounds.maxY + margin; y++) {
      for (let x = bounds.minX - margin; x <= bounds.maxX + margin; x++) {
        const bucket = this.buckets.get(`${x},${y}`);
        if (!bucket) continue;
        for (const placement of bucket) {
          if (!placement.light || hiddenRoof?.has(placement)) continue;
          const overhead = this.buckets.get(`${placement.x + 1},${placement.y + 1}`);
          const covered = overhead?.some(
            (candidate) =>
              !candidate.lightTransparent &&
              !hiddenRoof?.has(candidate) &&
              candidate.z >= placement.z + 5,
          );
          if (!covered) this.lightWindow.push(placement);
          if (this.lightWindow.length >= CLASSIC_UO_LIGHT_LIMIT) return this.lightWindow;
        }
      }
    }
    return this.lightWindow;
  }
}

/** Build indexed placement data without materializing full-map Pixi sprites. */
export function createStatics(map: GameMap, textures: StaticTextures): StaticScene {
  return new StaticScene(map, textures);
}
